import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import '../components/AdminLayout.css';
import './FavoriteInsights.css';

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('tr-TR') : '-'; }

export default function FavoriteInsights() {
  const [noteTable, setNoteTable] = useState([]);
  const [summary, setSummary] = useState({ total: 0, topNote: '-', topCourse: '-', topDept: '-' });
  const [popularCourses, setPopularCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const { data: favs, error } = await supabase
        .from('favorites')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) { console.error('Favorites error:', error); setLoading(false); return; }

      const noteIds = [...new Set((favs || []).map(f => f.note_id).filter(Boolean))];
      let noteMap = {};
      if (noteIds.length > 0) {
        const { data: notes } = await supabase.from('notes').select('id, title, course, department, user_id').in('id', noteIds);
        const userIds = [...new Set((notes || []).map(n => n.user_id).filter(Boolean))];
        let profileMap = {};
        if (userIds.length > 0) {
          const { data: profiles } = await supabase.from('profiles').select('id, name').in('id', userIds);
          if (profiles) profiles.forEach(p => { profileMap[p.id] = p.name; });
        }
        if (notes) notes.forEach(n => { noteMap[n.id] = { ...n, ownerName: profileMap[n.user_id] || '-' }; });
      }

      // Aggregate by note
      const noteCounts = {};
      (favs || []).forEach(f => {
        if (!f.note_id) return;
        if (!noteCounts[f.note_id]) noteCounts[f.note_id] = { note: noteMap[f.note_id] || { id: f.note_id }, count: 0, latest: f.created_at };
        noteCounts[f.note_id].count++;
        if (f.created_at > noteCounts[f.note_id].latest) noteCounts[f.note_id].latest = f.created_at;
      });
      const sorted = Object.values(noteCounts).sort((a, b) => b.count - a.count);

      // Course counts
      const courseCounts = {};
      (favs || []).forEach(f => {
        const c = noteMap[f.note_id]?.course;
        if (c) courseCounts[c] = (courseCounts[c] || 0) + 1;
      });
      const courseEntries = Object.entries(courseCounts).sort((a, b) => b[1] - a[1]);

      // Dept counts
      const deptCounts = {};
      (favs || []).forEach(f => {
        const d = noteMap[f.note_id]?.department;
        if (d) deptCounts[d] = (deptCounts[d] || 0) + 1;
      });
      const deptEntries = Object.entries(deptCounts).sort((a, b) => b[1] - a[1]);

      setSummary({
        total: favs?.length || 0,
        topNote: sorted[0]?.note?.course_name || sorted[0]?.note?.title || '-',
        topCourse: courseEntries[0]?.[0] || '-',
        topDept: deptEntries[0]?.[0] || '-',
      });
      setNoteTable(sorted);
      setPopularCourses(courseEntries.slice(0, 5).map(([course, count]) => ({ course, count })));
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  const SUMMARY_CARDS = [
    { label: 'Toplam Favori', value: summary.total, icon: '★', color: '#FFF8E6', iconColor: '#FFB400' },
    { label: 'En Çok Favorilenen Not', value: summary.topNote, icon: '📄', color: '#EEF2FD', iconColor: '#5784E6' },
    { label: 'En Popüler Ders', value: summary.topCourse, icon: '📈', color: '#E8F5E9', iconColor: '#388E3C' },
    { label: 'En Aktif Bölüm', value: summary.topDept, icon: '📈', color: '#EDE7F6', iconColor: '#7B1FA2' },
  ];

  return (
    <div>
      <div className="admin-stat-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {SUMMARY_CARDS.map(c => (
          <div className="admin-stat-card" key={c.label}>
            <div className="stat-card-icon" style={{ background: c.color }}>
              <span style={{ color: c.iconColor }}>{c.icon}</span>
            </div>
            <div className="stat-card-value" style={{ fontSize: '1.4rem' }}>{loading ? '—' : c.value}</div>
            <div className="stat-card-label">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="fav-grid">
        <div className="admin-section" style={{ flex: 2 }}>
          <div className="admin-section-header">
            <h2 className="admin-section-title">En Çok Favorilenen Notlar</h2>
          </div>
          <div className="admin-table-wrapper">
            <div className="admin-table-scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Not Başlığı</th>
                    <th>Ders</th>
                    <th>Bölüm</th>
                    <th>Sahip</th>
                    <th>Favori</th>
                    <th>Son Favori</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr className="admin-loading-row"><td colSpan={6}>Yükleniyor...</td></tr>
                  ) : noteTable.length === 0 ? (
                    <tr className="admin-loading-row"><td colSpan={6}>Favori bulunamadı.</td></tr>
                  ) : noteTable.map(({ note, count, latest }) => (
                    <tr key={note.id}>
                      <td><strong>{note.title || '-'}</strong></td>
                      <td>{note.course || '-'}</td>
                      <td>{note.department || '-'}</td>
                      <td>{note.ownerName || '-'}</td>
                      <td><span style={{ fontWeight: 700, color: '#FFB400' }}>★ {count}</span></td>
                      <td>{fmtDate(latest)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="admin-section" style={{ flex: 1 }}>
          <div className="admin-section-header">
            <h2 className="admin-section-title">Popüler Dersler</h2>
          </div>
          <div className="admin-table-wrapper" style={{ padding: '8px 0' }}>
            {loading ? (
              <div style={{ padding: 24, color: '#7A8BAD', fontSize: '0.875rem' }}>Yükleniyor...</div>
            ) : popularCourses.length === 0 ? (
              <div style={{ padding: 24, color: '#7A8BAD', fontSize: '0.875rem' }}>Veri yok.</div>
            ) : popularCourses.map((c, i) => (
              <div key={c.course} className="popular-course-row">
                <div className={`popular-rank rank-${i + 1}`}>{i + 1}</div>
                <div className="popular-course-name">{c.course}</div>
                <div className="popular-course-count">★ {c.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
